import React, { useState } from 'react';
import { Package, Loader2, Truck, Clock, CheckCircle, MapPin, CreditCard, Calendar } from 'lucide-react';
import axios from 'axios';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  pending: { label: 'Pending', color: 'text-yellow-600 bg-yellow-50 border-yellow-200', icon: Clock },
  confirmed: { label: 'Confirmed', color: 'text-blue-600 bg-blue-50 border-blue-200', icon: CheckCircle },
  shipped: { label: 'Shipped', color: 'text-purple-600 bg-purple-50 border-purple-200', icon: Truck },
  delivered: { label: 'Delivered', color: 'text-green-600 bg-green-50 border-green-200', icon: CheckCircle },
  cancelled: { label: 'Cancelled', color: 'text-red-600 bg-red-50 border-red-200', icon: Clock },
};

const TrackOrderModal = ({ open, onClose }) => {
  const [orderRef, setOrderRef] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const handleTrack = async (e) => {
    e.preventDefault();
    if (!orderRef.trim()) return;
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const res = await axios.get(`${API}/track-order/${encodeURIComponent(orderRef.trim())}`);
      setResult(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Order not found. Please check your order number.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setOrderRef('');
    setResult(null);
    setError('');
    onClose();
  };

  const statusConf = result ? (STATUS_CONFIG[result.status] || STATUS_CONFIG.pending) : null;
  const StatusIcon = statusConf?.icon || Clock;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md bg-white" data-testid="track-order-modal">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Package className="w-5 h-5" /> Track Your Order
          </DialogTitle>
        </DialogHeader>

        {!result ? (
          <form onSubmit={handleTrack} className="space-y-4 mt-2">
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block">
                Order Number
              </label>
              <Input
                value={orderRef}
                onChange={(e) => setOrderRef(e.target.value)}
                placeholder="e.g. #7359D447 or full order ID"
                className="border-gray-200 text-gray-900 text-base font-mono uppercase"
                autoFocus
                data-testid="track-order-input"
              />
              <p className="text-[11px] text-gray-400 mt-1">Enter the 8-character code from your confirmation page or email</p>
            </div>
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700" data-testid="track-order-error">
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={loading || !orderRef.trim()}
              className="w-full py-3 bg-gray-900 text-white text-sm font-bold uppercase tracking-wider rounded-lg hover:bg-gray-700 disabled:opacity-40 flex items-center justify-center gap-2 transition-colors"
              data-testid="track-order-submit"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Package className="w-4 h-4" />}
              Track Order
            </button>
          </form>
        ) : (
          <div className="space-y-4 mt-2" data-testid="track-order-result">
            {/* Order Header */}
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-400 uppercase tracking-wider">Order</p>
                <p className="text-xl font-mono font-black text-gray-900 tracking-wider" data-testid="track-order-ref">{result.order_ref}</p>
              </div>
              <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border ${statusConf.color}`} data-testid="track-order-status">
                <StatusIcon className="w-3.5 h-3.5" />
                {statusConf.label}
              </div>
            </div>

            {/* Status Timeline */}
            <div className="bg-gray-50 rounded-xl p-4">
              <div className="flex items-center gap-2">
                {['pending', 'confirmed', 'shipped', 'delivered'].map((step, i) => {
                  const steps = ['pending', 'confirmed', 'shipped', 'delivered'];
                  const currentIdx = steps.indexOf(result.status);
                  const isActive = i <= currentIdx;
                  return (
                    <React.Fragment key={step}>
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold transition-colors ${isActive ? 'bg-gray-900 text-white' : 'bg-gray-200 text-gray-400'}`}>
                        {isActive && i <= currentIdx ? '✓' : i + 1}
                      </div>
                      {i < 3 && <div className={`flex-1 h-0.5 ${i < currentIdx ? 'bg-gray-900' : 'bg-gray-200'}`} />}
                    </React.Fragment>
                  );
                })}
              </div>
              <div className="flex justify-between mt-2">
                {['Pending', 'Confirmed', 'Shipped', 'Delivered'].map(s => (
                  <span key={s} className="text-[10px] text-gray-500">{s}</span>
                ))}
              </div>
            </div>

            {/* Shipping info */}
            {result.status === 'confirmed' && (
              <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-1">
                  <Truck className="w-4 h-4 text-blue-600" />
                  <span className="text-xs font-bold text-blue-700">Estimated Shipping</span>
                </div>
                <p className="text-sm text-blue-800">Your order will ship within 2–3 business days. You'll receive a tracking number once it's on its way.</p>
              </div>
            )}

            {/* Tracking Number */}
            {result.tracking_number && (
              <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-1">
                  <Truck className="w-4 h-4 text-purple-600" />
                  <span className="text-xs font-bold text-purple-600 uppercase tracking-wider">Tracking Number</span>
                </div>
                <p className="text-sm font-mono font-bold text-purple-900" data-testid="track-order-tracking">{result.tracking_number}</p>
              </div>
            )}

            {/* Order Details */}
            <div className="border border-gray-200 rounded-xl overflow-hidden">
              <div className="bg-gray-50 px-4 py-2.5 border-b border-gray-200">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">Items</p>
              </div>
              {result.items.map((item, i) => (
                <div key={i} className="px-4 py-3 flex justify-between items-center border-b border-gray-100 last:border-0">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{item.title}</p>
                    <p className="text-xs text-gray-400">Size: {item.size} &middot; Qty: {item.quantity}</p>
                  </div>
                  {item.price > 0 && <span className="text-sm font-semibold text-gray-700">${(item.price * item.quantity).toFixed(2)}</span>}
                </div>
              ))}
              {result.discount > 0 && (
                <div className="px-4 py-2 flex justify-between border-b border-gray-100">
                  <span className="text-sm text-green-600">Discount</span>
                  <span className="text-sm font-medium text-green-600">-${result.discount.toFixed(2)}</span>
                </div>
              )}
              <div className="px-4 py-2 flex justify-between border-b border-gray-100">
                <span className="text-sm text-gray-500">Shipping</span>
                <span className="text-sm text-gray-500">{result.shipping === 0 ? 'FREE' : `$${result.shipping?.toFixed(2)}`}</span>
              </div>
              <div className="px-4 py-3 bg-gray-50 flex justify-between">
                <span className="text-sm font-bold text-gray-900">Total</span>
                <span className="text-sm font-bold text-gray-900">${result.total.toFixed(2)}</span>
              </div>
            </div>

            {/* Meta info */}
            <div className="flex flex-wrap gap-x-5 gap-y-2 text-xs text-gray-500">
              {result.shipping_address?.city && (
                <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {result.shipping_address.city}{result.shipping_address.state ? `, ${result.shipping_address.state}` : ''}</span>
              )}
              {result.paid_at && (
                <span className="flex items-center gap-1"><CreditCard className="w-3 h-3" /> Paid {new Date(result.paid_at).toLocaleDateString()}</span>
              )}
              {result.created_at && (
                <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> Ordered {new Date(result.created_at).toLocaleDateString()}</span>
              )}
            </div>

            <button
              onClick={() => { setResult(null); setOrderRef(''); }}
              className="w-full py-2.5 border border-gray-200 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors"
              data-testid="track-another-btn"
            >
              Track Another Order
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default TrackOrderModal;
